import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { X, Send, Sparkles, User, Phone, MessageCircle } from 'lucide-react';
import { NishaAvatar, useCurrentFounder, FOUNDERS } from './NishaAvatar';
import { getWhatsAppLink, getPhoneLink } from '../constants/contact';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GREETING = {
  id: 'greeting-0',
  role: 'assistant',
  founderKey: 'lisa',
  text:
    "Namaste! 🙏 Tell me what you're looking for — a plot, flat, or house? You can also ask about site visits, prices, mouza maps, or careers.",
};

const newMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Hoisted so React doesn't recreate the component tree on every AIChat render.
const MARKDOWN_COMPONENTS = {
  p: (props) => <p className="mb-1.5 last:mb-0" {...props} />,
  ul: (props) => <ul className="list-none pl-0 my-1 space-y-1" {...props} />,
  ol: (props) => <ol className="list-decimal pl-4 my-1 space-y-0.5" {...props} />,
  li: (props) => <li className="leading-snug" {...props} />,
  strong: (props) => <strong className="font-bold text-slate-900" {...props} />,
  a: ({ href, ...props }) => (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="text-emerald-700 font-semibold underline hover:text-emerald-900"
      {...props}
    />
  ),
  code: (props) => (
    <code className="bg-slate-100 text-slate-800 rounded px-1 py-0.5 text-[11px] font-mono" {...props} />
  ),
};

const QUICK_PROMPTS = [
  'Show me residential plots',
  '2BHK flats for rent',
  'Commercial land in Pune',
  'Book an appointment',
];

// Local FAQ layer — instant answers without a backend/LLM call.
// Saves credits for questions we already know the answer to.
const LOCAL_FAQ = [
  { match: /\b(who|what).*(acre ?mitra|company|about)\b/i,
    a: '**Acre Mitra** is a founder-led Indian real-estate platform for buying, selling and investing in verified plots, flats and land. We handle legal, geospatial and ROI due-diligence on every listing.' },
  { match: /\b(service|what.*(do|offer))\b/i,
    a: 'We offer: plot listings across India, AI Architect layouts, Land-Fill estimator, corridor investment waitlists (Pink Line / Kalyani Airport), on-site visits, and complete legal + registry support.' },
  { match: /\b(price|cost|fee|charge|commission)\b/i,
    a: 'Site listing is free for browsers. For sellers we charge a flat 1% commission at successful registration. Investment advisory calls are always free.' },
  { match: /\b(contact|phone|whatsapp|call|reach)\b/i,
    a: 'Call **+91 85973 10624** or WhatsApp us anytime. You can also click **Talk to an investment advisor** on the Projects page to schedule a free 15-min call at your slot.' },
  { match: /\b(book|schedule|appointment|visit|site visit)\b/i,
    a: 'Tap **Appointment** in the header, or open a property → *Schedule Site Visit*. Our advisor confirms within 30 min on WhatsApp.' },
  { match: /\b(list|sell).*(land|property|plot)\b/i,
    a: 'Great! Go to **List Property** in the header, or scroll to **Projects → List your own land** to open the seller form. We verify papers before publishing.' },
  { match: /\b(invest|waitlist|fractional|reit|corridor|pink line|kalyani)\b/i,
    a: 'We run corridor-specific waitlists for the **Pink Line Metro** and **Kalyani Greenfield Airport** corridors. Tap *Reserve a plot in this corridor* under News, or open **Investors** in the header.' },
  { match: /\b(unit|bigha|katha|guntha|kanal|marla|cent|ground|acre)\b/i,
    a: 'We support **all 28 states + 8 UTs** and their local land units — Katha (WB, Bihar, Assam), Bigha (UP, MP, Rajasthan, HP), Guntha (Maharashtra, Karnataka), Kanal / Marla (Punjab, Haryana, J&K), Ground / Cent (TN, Kerala, AP, Telangana), Ares (Kerala), Sq. Metre (Goa, Gujarat) and more. Every calculator auto-converts to sq ft.' },
  { match: /\b(cities|towns|villages|location|where)\b/i,
    a: 'Our listings cover every Indian state and UT — from Kolkata, Mumbai, Bangalore, Delhi, Chennai and Hyderabad to tier-2/3 towns and district HQs. Use the state + city dropdown in Projects to filter.' },
  { match: /\b(safe|verify|legal|mutation|title|rera)\b/i,
    a: 'Every plot on Acre Mitra passes a 4-step check: (1) enquiry logged, (2) panel lawyer / RERA broker screens papers, (3) mutation + HT-wire + registry cross-check, (4) buyer-only reviews. See our *How we verify* section on the Testimonials page.' },
  { match: /\b(architect|floor plan|design|vastu)\b/i,
    a: 'Our **AI Architect** generates 3 RERA-compliant + Vastu-checked floor plans for your plot dimensions in ~4 minutes. Open the **Architect** tab in the header.' },
  { match: /\b(land ?fill|soil|filling)\b/i,
    a: 'Use the **Land-Fill Estimator** under Explore → Land-Fill. Enter plot size + fill height and we return an itemised quote on Acre Mitra letterhead (accurate within 4% of actual).' },
];

const matchLocalFaq = (q) => {
  for (const item of LOCAL_FAQ) {
    if (item.match.test(q)) return item.a;
  }
  return null;
};

const getSessionId = () => {
  const key = 'acremitra_chat_session';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, id);
  }
  return id;
};

export const AIChat = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const currentFounder = useCurrentFounder();
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [turnCount, setTurnCount] = useState(0);
  const [leadCaptured, setLeadCaptured] = useState(
    () => !!localStorage.getItem('acremitra_chat_lead')
  );
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, showLeadForm]);

  useEffect(() => {
    // Subtle auto-popup after 8s to invite user; only once
    const key = 'acremitra_chat_autoshown';
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => {
      setExpanded(true);
      setTimeout(() => setExpanded(false), 4500);
      sessionStorage.setItem(key, '1');
    }, 8000);
    return () => clearTimeout(t);
  }, []);

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;

    // Intercept the "Book an appointment" quick prompt and open the booking modal directly
    if (/^book.*appointment/i.test(msg) || /book.*site.*visit/i.test(msg)) {
      window.dispatchEvent(new CustomEvent('openAppointmentModal'));
      setMessages((prev) => [
        ...prev,
        { id: newMessageId(), role: 'user', text: msg },
        {
          id: newMessageId(),
          role: 'assistant',
          founderKey: currentFounder.key,
          text:
            "Great! I've opened the appointment booking form for you. Pick a date, time slot, and a few details — our team will confirm shortly. 📅",
        },
      ]);
      setInput('');
      return;
    }

    setMessages((prev) => [...prev, { id: newMessageId(), role: 'user', text: msg }]);
    setInput('');
    setSending(true);

    // 0 credits: try local FAQ first
    const local = matchLocalFaq(msg);
    if (local) {
      // simulate small delay so it feels like a reply, not a static string
      setTimeout(() => {
        setMessages((prev) => [...prev, { id: newMessageId(), role: 'assistant', founderKey: currentFounder.key, text: local }]);
        setSending(false);
        const next = turnCount + 1;
        setTurnCount(next);
        if (next >= 3 && !leadCaptured) setTimeout(() => setShowLeadForm(true), 400);
      }, 350);
      return;
    }

    try {
      const payload = { session_id: sessionId, message: msg };
      const res = await axios.post(`${API}/chat`, payload);
      const reply = res.data?.reply || 'Sorry, I did not catch that. Could you try again?';
      setMessages((prev) => [...prev, { id: newMessageId(), role: 'assistant', founderKey: currentFounder.key, text: reply }]);
      const next = turnCount + 1;
      setTurnCount(next);
      if (next >= 3 && !leadCaptured) {
        setTimeout(() => setShowLeadForm(true), 400);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: newMessageId(),
          role: 'assistant',
          founderKey: currentFounder.key,
          text:
            '⚠️ Sorry, I am having trouble connecting right now. You can also reach us on WhatsApp +91 85973 10624.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadName.trim() || leadPhone.replace(/\D/g, '').length < 10) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: `My name is ${leadName}. Please contact me on ${leadPhone}.`,
        user_name: leadName.trim(),
        user_phone: leadPhone.replace(/\D/g, '').slice(-10),
      });
      localStorage.setItem('acremitra_chat_lead', '1');
      setLeadCaptured(true);
      setShowLeadForm(false);
      setMessages((prev) => [
        ...prev,
        { id: newMessageId(), role: 'user', text: `My name is ${leadName}, phone ${leadPhone}.` },
        {
          id: newMessageId(),
          role: 'assistant',
          founderKey: currentFounder.key,
          text: `Thanks ${leadName}! 🙏 One of our Acre Mitra advisors will call you on ${leadPhone} shortly. Meanwhile, feel free to keep asking anything.`,
        },
      ]);
    } catch (_) {
      setShowLeadForm(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
          {/* Side buttons: Call + WhatsApp */}
          <a
            href={getPhoneLink()}
            className="w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
            aria-label="Call Acre Mitra"
            data-testid="float-call"
          >
            <Phone size={20} />
          </a>
          <a
            href={getWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="w-12 h-12 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
            aria-label="WhatsApp Acre Mitra"
            data-testid="float-whatsapp"
          >
            <MessageCircle size={20} />
          </a>
          {/* Ask Nisha launcher - primary */}
          <button
            onClick={() => setOpen(true)}
            className="group relative bg-gradient-to-br from-emerald-600 to-emerald-800 hover:from-emerald-700 hover:to-emerald-900 text-white rounded-full shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 pl-2 pr-5 py-2 border-2 border-white"
            data-testid="ai-chat-launcher"
          >
            <NishaAvatar size={40} withStatus showBio bioPlacement="top" />
            <span className="font-semibold text-sm">Ask us</span>
          </button>
          {expanded && (
            <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl p-3 border border-slate-200 max-w-[240px] animate-fadeIn">
              <p className="text-sm text-slate-700">
                <strong className="text-emerald-700">Meet the founders</strong>
                <br />
                Lisa &amp; Sounav take turns here — ask about plots, prices, locations. Instant answers, always free.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-[92vw] max-w-[400px] h-[80vh] max-h-[640px] bg-white rounded-2xl shadow-2xl flex flex-col border border-slate-200 overflow-hidden animate-fadeIn">
          {/* Header */}
          <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white px-4 py-3 flex items-center gap-3">
            <NishaAvatar size={44} withStatus />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                Ask us
                <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
              </div>
              <div className="text-[11px] text-emerald-100">
                Lisa &amp; Sounav · Instant &amp; free
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white p-1"
              aria-label="Close chat"
              data-testid="ai-chat-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50"
            data-testid="ai-chat-messages"
          >
            {messages.map((m, i) => {
              const founder = m.founderKey ? FOUNDERS.find(f => f.key === m.founderKey) : null;
              return (
              <div
                key={m.id || `msg-${i}`}
                className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {m.role === 'assistant' && (
                  <NishaAvatar size={28} founderKey={m.founderKey} className="flex-shrink-0" />
                )}
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm break-words ${
                    m.role === 'user'
                      ? 'bg-emerald-700 text-white rounded-br-sm whitespace-pre-wrap'
                      : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {m.role === 'assistant' ? (
                    <>
                      {founder && (
                        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1" data-testid={`founder-signature-${founder.key}`}>
                          {founder.name}
                        </div>
                      )}
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                        {m.text}
                      </ReactMarkdown>
                    </>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.text}</span>
                  )}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center flex-shrink-0">
                    <User size={14} />
                  </div>
                )}
              </div>
              );
            })}

            {sending && (
              <div className="flex gap-2 justify-start">
                <NishaAvatar size={28} founderKey={currentFounder.key} talking className="flex-shrink-0" />
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }}></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }}></span>
                </div>
              </div>
            )}

            {/* Quick prompts for fresh conversations */}
            {messages.length === 1 && !sending && (
              <div className="pt-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 font-medium">
                  Quick questions
                </p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendMessage(p)}
                      className="text-xs px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-full hover:bg-emerald-50 hover:border-emerald-500 hover:text-emerald-700 transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Lead capture mini-form */}
          {showLeadForm && !leadCaptured && (
            <div className="border-t border-slate-200 bg-emerald-50/60 px-4 py-3">
              <form onSubmit={submitLead} className="space-y-2">
                <p className="text-xs text-slate-700">
                  💬 Want a human advisor to follow up? Leave your details:
                </p>
                <div className="flex gap-2">
                  <input
                    value={leadName}
                    onChange={(e) => setLeadName(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="lead-name-input"
                  />
                  <input
                    value={leadPhone}
                    onChange={(e) => setLeadPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit phone"
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="lead-phone-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={!leadName || leadPhone.length !== 10}
                    className="flex-1 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white text-sm rounded-lg py-2 font-medium transition-colors"
                    data-testid="lead-submit"
                  >
                    Submit
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowLeadForm(false)}
                    className="px-3 text-sm text-slate-600 hover:text-slate-800"
                  >
                    Later
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Composer */}
          <div className="border-t border-slate-200 bg-white px-3 py-2.5">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask us about plots, prices, site visits…"
                className="flex-1 px-4 py-2.5 text-sm bg-slate-100 border border-transparent rounded-full focus:outline-none focus:bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                disabled={sending}
                data-testid="ai-chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="w-10 h-10 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white flex items-center justify-center transition-colors"
                data-testid="ai-chat-send"
              >
                <Send size={18} />
              </button>
            </form>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              For anything we can&rsquo;t answer, our team is a WhatsApp away · We respect your privacy
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChat;
